/**
 * YD Product Sequence 2 Mobile Section
 * 移动端专用的产品序列帧动画区块
 *
 * 功能:
 * - 阶段一: 序列帧 scrub 到全透明
 * - 阶段二: 8 星球滚动 scrub 切换（水平轮播视觉）
 * - 阶段三: 序列帧 Scrub + 标题动画
 */
(function() {
  'use strict';

  // 仅在移动端执行 (< 768px)
  if (window.innerWidth >= 768) {
    return;
  }

  /**
   * 视口高度锁定器 - 防止 Safari 地址栏变化引起抖动
   * 只在横竖屏切换或显著尺寸变化时才更新高度
   */
  const viewportHeightManager = {
    lockedHeight: null,
    lastOrientation: null,
    resizeCallbacks: [],

    initialize() {
      this.lockedHeight = window.innerHeight;
      this.lastOrientation = this.getOrientation();
      this.applyHeight();

      // 统一管理 resize 事件
      window.addEventListener('resize', () => this.handleResize());
    },

    getOrientation() {
      return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
    },

    handleResize() {
      const newOrientation = this.getOrientation();
      const heightDiff = Math.abs(window.innerHeight - this.lockedHeight);

      // 只在横竖屏切换 或 高度变化超过 100px 时才更新
      if (newOrientation !== this.lastOrientation || heightDiff > 100) {
        this.lockedHeight = window.innerHeight;
        this.lastOrientation = newOrientation;
        this.applyHeight();

        // 触发所有注册的回调
        this.resizeCallbacks.forEach(cb => cb());
      }
      // 忽略地址栏引起的小变化，不触发任何回调
    },

    applyHeight() {
      document.documentElement.style.setProperty('--vh-locked', `${this.lockedHeight}px`);
    },

    getHeight() {
      return this.lockedHeight;
    },

    // 注册需要在真正 resize 时执行的回调
    onResize(callback) {
      if (typeof callback === 'function') {
        this.resizeCallbacks.push(callback);
      }
    }
  };

  /**
   * 图片格式检测工具 (复用自桌面端)
   */
  const imageFormatDetector = {
    _cache: {},

    async isSupported(format) {
      if (this._cache[format] !== undefined) {
        return this._cache[format];
      }

      if (format === 'avif') {
        const result = await this._checkAvifSupport();
        this._cache[format] = result;
        return result;
      }

      return false;
    },

    _checkAvifSupport() {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.width > 0 && img.height > 0);
        img.onerror = () => resolve(false);
        img.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKBzgABogQEAwgMgwYAAAAKAAAAB4A';
        setTimeout(() => resolve(false), 1000);
      });
    }
  };

  /**
   * 星球时间控制器 (移动端简化版 - 单一固定显示元素)
   */
  class PlanetTimeController {
    constructor(planets) {
      this.planets = planets;
      this.planetConfigs = {};
      this.updateInterval = null;
      this.isRunning = false;
      this.activePlanetId = null;

      // 固定显示元素
      this.timeDisplay = null;
    }

    initialize() {
      // 缓存固定显示元素
      this.timeDisplay = document.querySelector('#planet-time-display');

      // 构建星球配置
      for (const planet of this.planets) {
        this.planetConfigs[planet.id] = {
          rotationPeriodHours: planet.rotationPeriodHours
        };
      }
    }

    startTimeUpdate(planetId) {
      if (this.activePlanetId === planetId && this.isRunning) return;

      this.activePlanetId = planetId;
      this.stopTimeUpdate();

      // 立即更新一次
      this.updateTime();

      // 设置定时更新
      this.updateInterval = setInterval(() => this.updateTime(), 1000);
      this.isRunning = true;
    }

    stopTimeUpdate() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
      this.isRunning = false;
    }

    updateTime() {
      if (!this.activePlanetId || !this.timeDisplay) return;

      const planetConfig = this.planetConfigs[this.activePlanetId];
      if (!planetConfig) return;

      const earthTime = new Date();
      const convertedTime = this.convertToPlanetTime(earthTime, planetConfig.rotationPeriodHours);

      this.timeDisplay.textContent = convertedTime;
    }

    convertToPlanetTime(earthTime, rotationPeriodHours) {
      const earthSeconds =
        earthTime.getHours() * 3600 +
        earthTime.getMinutes() * 60 +
        earthTime.getSeconds();

      const earthDaySeconds = 24 * 3600;
      const planetDaySeconds = rotationPeriodHours * 3600;

      const planetSeconds = earthSeconds * (earthDaySeconds / planetDaySeconds);

      const hours = Math.floor(planetSeconds / 3600) % 24;
      const minutes = Math.floor((planetSeconds % 3600) / 60);
      const seconds = Math.floor(planetSeconds % 60);

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    dispose() {
      this.stopTimeUpdate();
      this.planetConfigs = {};
      this.activePlanetId = null;
    }
  }

  /**
   * 移动端序列帧加载器 (支持多范围加载)
   */
  class MobileSequenceLoader {
    constructor(config) {
      this.basePath = config.framePath;
      this.avifPath = config.avifPath;
      this.fallbackPath = config.fallbackPath;
      this.images = {};  // 使用对象存储，支持稀疏帧
      this.loadedCount = 0;
      this.totalToLoad = 0;
      this.actualFormat = null;

      // 帧范围配置
      this.frameRanges = [];
    }

    getFramePath(frameNumber) {
      const paddedFrame = String(frameNumber).padStart(5, '0');
      if (this.actualFormat === 'avif') {
        return this.basePath + this.avifPath.replace('[frame]', paddedFrame);
      } else {
        return this.basePath + this.fallbackPath.replace('[frame]', paddedFrame);
      }
    }

    /**
     * 预加载指定范围的帧
     * @param {number} startFrame - 起始帧
     * @param {number} endFrame - 结束帧
     * @param {number} priorityFrames - 优先加载的帧数
     * @param {function} onProgress - 进度回调
     */
    async preloadFrameRange(startFrame, endFrame, priorityFrames = 50, onProgress) {
      // 检测格式 (只在首次调用时)
      if (this.actualFormat === null) {
        const supportsAvif = await imageFormatDetector.isSupported('avif');
        this.actualFormat = supportsAvif ? 'avif' : 'fallback';
      }

      // 记录范围
      this.frameRanges.push({ startFrame, endFrame });

      const totalInRange = endFrame - startFrame + 1;
      this.totalToLoad += totalInRange;

      // 优先加载前 N 帧
      const priorityEnd = Math.min(startFrame + priorityFrames, endFrame + 1);
      const priorityPromises = [];

      for (let i = startFrame; i < priorityEnd; i++) {
        const promise = this.loadSingleFrame(i, onProgress);
        priorityPromises.push(promise);
      }

      await Promise.all(priorityPromises);
    }

    /**
     * 后台继续加载剩余帧
     */
    continuePreload(onProgress) {
      const remainingFrames = [];

      for (const range of this.frameRanges) {
        for (let i = range.startFrame; i <= range.endFrame; i++) {
          if (!this.images[i]) {
            remainingFrames.push(i);
          }
        }
      }

      if (remainingFrames.length === 0) {
        return Promise.resolve();
      }

      // 分批加载，避免同时请求过多
      const batchSize = 10;
      const loadBatch = async (batch) => {
        const promises = batch.map(frameNumber =>
          this.loadSingleFrame(frameNumber, onProgress)
        );
        await Promise.all(promises);
      };

      const loadAllBatches = async () => {
        for (let i = 0; i < remainingFrames.length; i += batchSize) {
          const batch = remainingFrames.slice(i, i + batchSize);
          await loadBatch(batch);
        }
      };

      return loadAllBatches()
        .catch(() => {});
    }

    loadSingleFrame(frameNumber, onProgress) {
      return new Promise((resolve, reject) => {
        // 如果已加载，直接返回
        if (this.images[frameNumber]) {
          resolve(this.images[frameNumber]);
          return;
        }

        const img = new Image();
        const framePath = this.getFramePath(frameNumber);

        img.onload = () => {
          this.images[frameNumber] = img;
          this.loadedCount++;
          if (onProgress) {
            onProgress(this.loadedCount / this.totalToLoad);
          }
          resolve(img);
        };

        img.onerror = () => {
          resolve(null);
        };

        img.src = framePath;
      });
    }

    getFrame(frameNumber) {
      return this.images[frameNumber] || null;
    }

    getFrameSafe(frameNumber) {
      const img = this.getFrame(frameNumber);
      if (img) return img;

      // 向前查找最近的帧
      for (let offset = 1; offset <= 50; offset++) {
        const fallbackImg = this.getFrame(frameNumber - offset);
        if (fallbackImg) return fallbackImg;
      }

      // 向后查找
      for (let offset = 1; offset <= 50; offset++) {
        const fallbackImg = this.getFrame(frameNumber + offset);
        if (fallbackImg) return fallbackImg;
      }

      return null;
    }

    dispose() {
      this.images = {};
      this.loadedCount = 0;
      this.totalToLoad = 0;
      this.frameRanges = [];
    }
  }

  /**
   * Canvas 渲染器 (复用自桌面端)
   */
  class CanvasRenderer {
    constructor(canvasId) {
      this.canvas = document.querySelector(canvasId);
      if (!this.canvas) {
        throw new Error(`Canvas not found: ${canvasId}`);
      }

      this.ctx = this.canvas.getContext('2d');
      this.resizeCanvas();

      // 使用 viewportHeightManager 统一管理，只在真正 resize 时才重绘
      viewportHeightManager.onResize(() => this.resizeCanvas());
    }

    resizeCanvas() {
      // 移动端强制 DPR = 1，桌面端最大 2（性能优化）
      const isMobile = window.innerWidth < 768;
      const dpr = isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);
      const rect = this.canvas.getBoundingClientRect();

      this.canvas.width = rect.width * dpr;
      this.canvas.height = rect.height * dpr;

      this.ctx.scale(dpr, dpr);
      this.canvasWidth = rect.width;
      this.canvasHeight = rect.height;
    }

    renderFrame(img, opacity = 1) {
      if (!img) return;

      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      // 设置全局透明度
      this.ctx.globalAlpha = opacity;

      const imgAspect = img.width / img.height;
      const canvasAspect = this.canvasWidth / this.canvasHeight;

      let drawWidth, drawHeight, drawX, drawY;

      if (imgAspect > canvasAspect) {
        drawHeight = this.canvasHeight;
        drawWidth = this.canvasHeight * imgAspect;
        drawX = (this.canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = this.canvasWidth;
        drawHeight = this.canvasWidth / imgAspect;
        drawX = 0;
        drawY = (this.canvasHeight - drawHeight) / 2;
      }

      this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

      // 重置透明度
      this.ctx.globalAlpha = 1;
    }

    /**
     * 带缩放的帧渲染（用于 Phase 1 放大淡出效果）
     */
    renderFrameWithScale(img, opacity = 1, scale = 1) {
      if (!img) return;

      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.ctx.globalAlpha = opacity;

      const imgAspect = img.width / img.height;
      const canvasAspect = this.canvasWidth / this.canvasHeight;

      let baseWidth, baseHeight, baseX, baseY;

      if (imgAspect > canvasAspect) {
        baseHeight = this.canvasHeight;
        baseWidth = this.canvasHeight * imgAspect;
        baseX = (this.canvasWidth - baseWidth) / 2;
        baseY = 0;
      } else {
        baseWidth = this.canvasWidth;
        baseHeight = this.canvasWidth / imgAspect;
        baseX = 0;
        baseY = (this.canvasHeight - baseHeight) / 2;
      }

      // 应用缩放（从中心点缩放）
      const scaledWidth = baseWidth * scale;
      const scaledHeight = baseHeight * scale;
      const scaledX = baseX - (scaledWidth - baseWidth) / 2;
      const scaledY = baseY - (scaledHeight - baseHeight) / 2;

      this.ctx.drawImage(img, scaledX, scaledY, scaledWidth, scaledHeight);
      this.ctx.globalAlpha = 1;
    }

    clear() {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    }
  }

  /**
   * 阶段一控制器 - 序列帧 scrub + 放大淡出到全黑
   *
   * 交互流程:
   * 1. 序列帧 scrub (0-50帧)
   * 2. 放大 + 淡出到全黑 (最后一帧 scale 1→1.3, opacity 1→0)
   */
  class Phase1Controller {
    constructor(config, loader, renderer) {
      this.config = config;
      this.loader = loader;
      this.renderer = renderer;

      this.totalFrames = config.totalFrames;
      this.totalScrollDistance = 0;

      // 序列帧 scrub 占总滚动的比例 (默认 0.38)
      this.sequenceScrollRatio = config.phase1?.sequenceScrollRatio || 0.38;
      // 放大倍数 (默认 1.3)
      this.zoomScale = config.phase1?.zoomScale || 1.3;

      this.titleElements = {};
      this.currentFrame = 0;
      this.lastRenderedFrame = -1;
      this.lastScale = 1;
      this.lastOpacity = 1;

      this.scrollTrigger = null;
    }

    parseScrollHeight(value) {
      const viewportHeight = window.innerHeight;
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && (value.endsWith('vh') || value.endsWith('svh'))) {
        return parseFloat(value) / 100 * viewportHeight;
      }
      return viewportHeight;
    }

    initialize() {
      this.totalScrollDistance = this.parseScrollHeight(this.config.scrollHeight);

      // 缓存标题元素
      this.cacheTitleElements();

      // 设置 section 高度
      this.setupScrollSpacer();

      // 尝试渲染第一帧（如果帧已加载）
      this.renderFrame(0, 1);

      // 初始化标题
      this.updateTitles(0);

      // 创建 ScrollTrigger（不等待帧加载完成）
      this.createScrollTrigger();
    }

    cacheTitleElements() {
      const titles = this.config.phase1?.titles || [];
      for (const titleConfig of titles) {
        if (titleConfig.id) {
          const el = document.querySelector(`#${titleConfig.id}`);
          if (el) {
            this.titleElements[titleConfig.id] = el;
            // 标题初始状态：如果没有 fadeIn 配置，默认显示
            const initialOpacity = titleConfig.fadeIn ? 0 : 1;
            gsap.set(el, { opacity: initialOpacity });
          }
        }
      }
    }

    setupScrollSpacer() {
      // 不再手动设置高度，由 ScrollTrigger 的 pinSpacing 自动处理
    }

    createScrollTrigger() {
      if (typeof ScrollTrigger === 'undefined') {
        return;
      }

      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距
      this.scrollTrigger = ScrollTrigger.create({
        trigger: '#seq2-mobile-canvas-wrapper',
        start: 'top top',
        end: `+=${this.totalScrollDistance}`,
        pin: '#seq2-mobile-canvas-wrapper',
        pinSpacing: true,
        scrub: 1,
        onUpdate: (self) => this.handleScrollUpdate(self.progress)
      });
    }

    handleScrollUpdate(progress) {
      const sequenceRatio = this.sequenceScrollRatio;  // 序列帧阶段占比 (0.38)
      const zoomRatio = 1 - sequenceRatio;              // 放大淡出阶段占比 (0.62)

      if (progress <= sequenceRatio) {
        // === 阶段 A: 序列帧 scrub ===
        const sequenceProgress = progress / sequenceRatio;  // 0 → 1
        const currentFrame = Math.floor(sequenceProgress * (this.totalFrames - 1));

        // 正常渲染，不缩放
        this.renderFrame(currentFrame, 1, 1);

        // 更新标题
        this.updateTitles(currentFrame);

      } else {
        // === 阶段 B: 放大 + 淡出到全黑 ===
        const zoomProgress = (progress - sequenceRatio) / zoomRatio;  // 0 → 1

        // 保持最后一帧
        const lastFrame = this.totalFrames - 1;

        // 计算 scale: 1.0 → zoomScale (1.3)
        const scale = 1 + ((this.zoomScale - 1) * zoomProgress);

        // 计算 opacity: 1.0 → 0.0
        const opacity = 1 - zoomProgress;

        // 渲染带缩放的最后一帧
        this.renderFrame(lastFrame, opacity, scale);

        // 标题在序列帧结束时已淡出，但也检查一下
        this.updateTitles(lastFrame);
      }
    }

    renderFrame(frameNumber, opacity = 1, scale = 1) {
      // 检查是否需要重新渲染
      if (frameNumber === this.lastRenderedFrame &&
          opacity === this.lastOpacity &&
          scale === this.lastScale) {
        return;
      }

      this.currentFrame = frameNumber;
      this.lastRenderedFrame = frameNumber;
      this.lastOpacity = opacity;
      this.lastScale = scale;

      const img = this.loader.getFrameSafe(frameNumber);
      if (img) {
        // 使用带缩放的渲染方法
        this.renderer.renderFrameWithScale(img, opacity, scale);
      }
    }

    updateTitles(currentFrame) {
      const titles = this.config.phase1?.titles || [];

      for (const titleConfig of titles) {
        const el = this.titleElements[titleConfig.id];
        if (!el) continue;

        let opacity = 1;

        // 淡入逻辑
        if (titleConfig.fadeIn) {
          const { startFrame, endFrame } = titleConfig.fadeIn;
          if (currentFrame < startFrame) {
            opacity = 0;
          } else if (currentFrame < endFrame) {
            opacity = (currentFrame - startFrame) / (endFrame - startFrame);
          }
        }

        // 淡出逻辑
        if (titleConfig.fadeOut && opacity > 0) {
          const { startFrame, endFrame } = titleConfig.fadeOut;
          if (currentFrame >= endFrame) {
            opacity = 0;
          } else if (currentFrame > startFrame) {
            opacity = 1 - (currentFrame - startFrame) / (endFrame - startFrame);
          }
        }

        gsap.set(el, { opacity: Math.max(0, Math.min(1, opacity)) });
      }
    }

    dispose() {
      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }
    }
  }

  /**
   * 阶段二控制器 - 滚动 scrub 切换（保持轮播水平滑动视觉）
   */
  class PlanetScrubController {
    constructor(config, timeController) {
      this.config = config;
      this.timeController = timeController;

      // 容器和滑块元素
      this.container = document.querySelector('.planet-images-container');
      this.wrapper = document.querySelector('.planet-images-wrapper');
      this.slides = document.querySelectorAll('.planet-slide');

      // 固定显示元素
      this.displays = {
        cycle: document.querySelector('#planet-cycle-display'),
        name: document.querySelector('#planet-name-display'),
        lst: document.querySelector('#planet-lst-display'),
        o2: document.querySelector('#planet-o2-display'),
        grav: document.querySelector('#planet-grav-display'),
        dia: document.querySelector('#planet-dia-display')
      };

      // 星球数据 (从页面 JSON 加载)
      this.planetsData = [];

      // 滚动状态
      this.currentIndex = 0;
      this.slideWidth = 0;
      this.scrollTrigger = null;
    }

    parseScrollHeight(value) {
      const viewportHeight = window.innerHeight;
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && (value.endsWith('vh') || value.endsWith('svh'))) {
        return parseFloat(value) / 100 * viewportHeight;
      }
      return viewportHeight * 2;  // 默认 200svh
    }

    initialize() {
      // 加载星球数据
      this.loadPlanetsData();

      if (!this.container || !this.wrapper || this.slides.length === 0) {
        return;
      }

      // 计算尺寸
      this.calculateDimensions();

      // 设置 wrapper 宽度
      this.setupLayout();

      // 创建 ScrollTrigger
      this.createScrollTrigger();

      // 初始化显示第一个星球数据
      this.updateDisplays(0, false);

      // 启动第一个星球的时间
      const firstPlanet = this.planetsData[0];
      if (firstPlanet) {
        this.timeController.startTimeUpdate(firstPlanet.id);
      }

      // 使用 viewportHeightManager 统一管理，只在真正 resize 时才刷新
      viewportHeightManager.onResize(() => {
        this.calculateDimensions();
        this.setupLayout();
        ScrollTrigger.refresh();
      });
    }

    calculateDimensions() {
      // 滑块宽度 = 容器宽度（满屏宽）
      this.slideWidth = this.container.offsetWidth;
    }

    setupLayout() {
      // wrapper 宽度 = 滑块数量 × 滑块宽度
      this.wrapper.style.width = `${this.slides.length * this.slideWidth}px`;
      // 不设置 transition，由滚动进度直接控制

      // 每个滑块设置宽度
      this.slides.forEach(slide => {
        slide.style.width = `${this.slideWidth}px`;
      });
    }

    loadPlanetsData() {
      const jsonScript = document.querySelector('#planet-data-json');
      if (jsonScript) {
        try {
          this.planetsData = JSON.parse(jsonScript.textContent);
        } catch {
          this.planetsData = this.config.phase2?.planets || [];
        }
      } else {
        this.planetsData = this.config.phase2?.planets || [];
      }
    }

    createScrollTrigger() {
      const scrollHeight = this.parseScrollHeight(this.config.phase2?.scrollHeight || '200svh');

      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距
      this.scrollTrigger = ScrollTrigger.create({
        trigger: '#seq2-mobile-phase2-inner',
        start: 'top top',
        end: `+=${scrollHeight}`,
        pin: true,
        pinSpacing: true,
        scrub: 0.5,
        onUpdate: (self) => this.handleScrollUpdate(self.progress)
      });
    }

    handleScrollUpdate(progress) {
      // 计算当前应该滑动到哪个位置
      // progress: 0 → 1，对应第 0 个到第 7 个星球
      const totalSlides = this.slides.length;  // 8
      const maxOffset = (totalSlides - 1) * this.slideWidth;

      // translateX 从 0 到 -maxOffset
      const offset = -progress * maxOffset;
      this.wrapper.style.transform = `translateX(${offset}px)`;

      // 计算当前星球索引
      const newIndex = Math.round(progress * (totalSlides - 1));

      // 当星球切换时更新数据显示
      if (newIndex !== this.currentIndex) {
        this.updateDisplays(newIndex, true);
        this.currentIndex = newIndex;

        // 更新时间
        const planet = this.planetsData[newIndex];
        if (planet) {
          this.timeController.startTimeUpdate(planet.id);
        }
      }
    }

    updateDisplays(index, animate = true) {
      const planet = this.planetsData[index];
      if (!planet) return;

      const updateText = (el, text) => {
        if (!el) return;

        if (animate) {
          // Line reveal 风格动画：先下滑出去，更新文字，再上滑进来
          gsap.to(el, {
            yPercent: 100,
            opacity: 0,
            duration: 0.2,
            ease: 'power2.in',
            onComplete: () => {
              el.textContent = text;
              gsap.fromTo(el,
                { yPercent: -100, opacity: 0 },
                { yPercent: 0, opacity: 1, duration: 0.3, ease: 'power2.out' }
              );
            }
          });
        } else {
          el.textContent = text;
          gsap.set(el, { yPercent: 0, opacity: 1 });
        }
      };

      // 更新各项数据
      updateText(this.displays.cycle, planet.cycle || '');
      updateText(this.displays.name, planet.name || '');
      updateText(this.displays.lst, planet.lst || '');
      updateText(this.displays.o2, planet.o2 || '');
      updateText(this.displays.grav, planet.grav || '');
      updateText(this.displays.dia, planet.dia || '');
    }

    dispose() {
      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }
    }
  }

  /**
   * 阶段三控制器 - 序列帧 Scrub + 标题动画
   */
  class Phase3Controller {
    constructor(config, loader) {
      this.config = config;
      this.loader = loader;

      // 帧范围 (0-350，使用独立的帧资源)
      this.totalFrames = config.phase3?.totalFrames || 351;
      this.totalScrollDistance = 0;

      // Canvas 渲染器
      this.canvas = null;
      this.ctx = null;
      this.canvasWidth = 0;
      this.canvasHeight = 0;

      // 标题元素
      this.titleElements = {};
      this.titleStates = {};  // 跟踪标题显示状态

      this.currentFrame = 0;
      this.lastRenderedFrame = -1;
      this.scrollTrigger = null;
    }

    parseScrollHeight(value) {
      const viewportHeight = window.innerHeight;
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && (value.endsWith('vh') || value.endsWith('svh'))) {
        return parseFloat(value) / 100 * viewportHeight;
      }
      return viewportHeight * 4;  // 默认 400svh
    }

    initialize() {
      this.totalScrollDistance = this.parseScrollHeight(this.config.phase3?.scrollHeight);

      // 初始化 Canvas
      this.initCanvas();

      // 缓存标题元素
      this.cacheTitleElements();

      // 设置 section 高度
      this.setupScrollSpacer();

      // 渲染第一帧
      this.renderFrame(0);

      // 初始化标题 (全部隐藏)
      Object.values(this.titleElements).forEach(el => {
        gsap.set(el, { opacity: 0 });
        const lines = el.querySelectorAll('.title-line');
        lines.forEach(line => gsap.set(line, { yPercent: 100 }));
      });

      // 创建 ScrollTrigger
      this.createScrollTrigger();
    }

    initCanvas() {
      this.canvas = document.querySelector('#seq2-mobile-phase3-canvas');
      if (!this.canvas) {
        return;
      }

      this.ctx = this.canvas.getContext('2d');
      this.resizeCanvas();

      // 使用 viewportHeightManager 统一管理，只在真正 resize 时才重绘
      viewportHeightManager.onResize(() => this.resizeCanvas());
    }

    resizeCanvas() {
      if (!this.canvas) return;

      // 移动端强制 DPR = 1，桌面端最大 2（性能优化）
      const isMobile = window.innerWidth < 768;
      const dpr = isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);
      const rect = this.canvas.getBoundingClientRect();

      this.canvas.width = rect.width * dpr;
      this.canvas.height = rect.height * dpr;

      this.ctx.scale(dpr, dpr);
      this.canvasWidth = rect.width;
      this.canvasHeight = rect.height;

      // 重新渲染当前帧
      if (this.lastRenderedFrame >= 0) {
        this.renderFrame(this.lastRenderedFrame, true);
      }
    }

    cacheTitleElements() {
      const titles = this.config.phase3?.titles || [];
      for (const titleConfig of titles) {
        if (titleConfig.id) {
          const el = document.querySelector(`#${titleConfig.id}`);
          if (el) {
            this.titleElements[titleConfig.id] = el;
            this.titleStates[titleConfig.id] = 'hidden';
          }
        }
      }
    }

    setupScrollSpacer() {
      // 不再手动设置高度，由 ScrollTrigger 的 pinSpacing 自动处理
    }

    createScrollTrigger() {
      if (typeof ScrollTrigger === 'undefined') {
        return;
      }

      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距
      this.scrollTrigger = ScrollTrigger.create({
        trigger: '#seq2-mobile-phase3-canvas-wrapper',
        start: 'top top',
        end: `+=${this.totalScrollDistance}`,
        pin: '#seq2-mobile-phase3-canvas-wrapper',
        pinSpacing: true,
        scrub: 1,
        onUpdate: (self) => this.handleScrollUpdate(self.progress)
      });
    }

    handleScrollUpdate(progress) {
      // 计算帧号 (0-350，使用独立帧资源)
      const frameNumber = Math.floor(progress * (this.totalFrames - 1));

      // 渲染当前帧
      this.renderFrame(frameNumber);

      // 更新标题
      this.updateTitles(frameNumber);
    }

    renderFrame(frameNumber, forceRender = false) {
      if (frameNumber === this.lastRenderedFrame && !forceRender) return;

      this.currentFrame = frameNumber;
      this.lastRenderedFrame = frameNumber;

      const img = this.loader.getFrameSafe(frameNumber);
      if (img && this.ctx) {
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

        const imgAspect = img.width / img.height;
        const canvasAspect = this.canvasWidth / this.canvasHeight;

        let drawWidth, drawHeight, drawX, drawY;

        if (imgAspect > canvasAspect) {
          drawHeight = this.canvasHeight;
          drawWidth = this.canvasHeight * imgAspect;
          drawX = (this.canvasWidth - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = this.canvasWidth;
          drawHeight = this.canvasWidth / imgAspect;
          drawX = 0;
          drawY = (this.canvasHeight - drawHeight) / 2;
        }

        this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
      }
    }

    updateTitles(relativeFrame) {
      const titles = this.config.phase3?.titles || [];

      for (const titleConfig of titles) {
        const el = this.titleElements[titleConfig.id];
        if (!el) continue;

        const currentState = this.titleStates[titleConfig.id];
        let targetState = 'hidden';

        // 判断应该显示还是隐藏
        const fadeIn = titleConfig.fadeIn;
        const fadeOut = titleConfig.fadeOut;

        if (fadeIn) {
          if (relativeFrame >= fadeIn.endFrame) {
            // 已完成淡入
            if (fadeOut) {
              if (relativeFrame < fadeOut.startFrame) {
                targetState = 'visible';
              } else if (relativeFrame >= fadeOut.endFrame) {
                targetState = 'hidden';
              } else {
                // 正在淡出中
                targetState = 'fading-out';
              }
            } else {
              // 没有淡出配置，保持显示
              targetState = 'visible';
            }
          } else if (relativeFrame >= fadeIn.startFrame) {
            // 正在淡入中
            targetState = 'fading-in';
          }
        }

        // 执行状态转换
        if (currentState !== targetState) {
          this.transitionTitle(el, titleConfig.id, currentState, targetState);
        }
      }
    }

    transitionTitle(el, id, fromState, toState) {
      const lines = el.querySelectorAll('.title-line');

      if (toState === 'fading-in' || toState === 'visible') {
        if (fromState === 'hidden') {
          // 从隐藏到显示: line-reveal 动画
          gsap.set(el, { opacity: 1 });
          lines.forEach((line, i) => {
            gsap.fromTo(line,
              { yPercent: 100 },
              {
                yPercent: 0,
                duration: 0.5,
                delay: i * 0.1,
                ease: 'power2.out'
              }
            );
          });
        }
        this.titleStates[id] = 'visible';
      } else if (toState === 'fading-out' || toState === 'hidden') {
        if (fromState === 'visible' || fromState === 'fading-in') {
          // 从显示到隐藏: line-reveal 反向动画
          lines.forEach((line, i) => {
            gsap.to(line, {
              yPercent: -100,
              duration: 0.4,
              delay: i * 0.05,
              ease: 'power2.in',
              onComplete: () => {
                if (i === lines.length - 1) {
                  gsap.set(el, { opacity: 0 });
                  // 重置 yPercent 为下次入场准备
                  lines.forEach(l => gsap.set(l, { yPercent: 100 }));
                }
              }
            });
          });
        }
        this.titleStates[id] = 'hidden';
      }
    }

    dispose() {
      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }
    }
  }

  /**
   * 等待依赖加载
   */
  function waitForDependencies() {
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          clearInterval(check);
          gsap.registerPlugin(ScrollTrigger);
          resolve();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(check);
      }, 10000);
    });
  }

  /**
   * 初始化
   */
  async function init() {
    const section = document.querySelector('#product-sequence-section-2-mobile');
    if (!section) {
      return;
    }

    const configData = section.dataset.config;
    if (!configData) {
      return;
    }

    const config = JSON.parse(configData);

    // 初始化视口高度管理器（防止 Safari 地址栏变化引起抖动）
    viewportHeightManager.initialize();

    // 等待依赖
    await waitForDependencies();

    try {
      // Phase 1 帧加载器 (帧 0-50，共 51 帧)
      const phase1Loader = new MobileSequenceLoader({
        framePath: config.framePath,
        avifPath: config.avifPath,
        fallbackPath: config.fallbackPath
      });

      // Phase 3 帧加载器 (独立的帧资源 0-350)
      const phase3Config = config.phase3 || {};
      const phase3Loader = new MobileSequenceLoader({
        framePath: phase3Config.framePath || 'https://cf-files.dotnfilm.studio/gravity_web/frames/light_end_mobile/',
        avifPath: phase3Config.avifPath || 'avif/1_[frame].avif',
        fallbackPath: phase3Config.fallbackPath || 'webp/1_[frame].webp'
      });

      // 1. 阶段一: 序列帧 - 先创建 ScrollTrigger，后台加载帧
      const renderer = new CanvasRenderer('#seq2-mobile-canvas');
      const phase1 = new Phase1Controller(config, phase1Loader, renderer);
      phase1.initialize();

      // 2. 阶段二: 星球滚动切换
      const planets = config.phase2?.planets || [];
      const timeController = new PlanetTimeController(planets);
      timeController.initialize();

      const planetScrub = new PlanetScrubController(config, timeController);
      planetScrub.initialize();

      // 3. 阶段三: 序列帧 Scrub + 标题 - 先创建 ScrollTrigger，后台加载帧
      const phase3 = new Phase3Controller(config, phase3Loader);
      phase3.initialize();

      // 存储实例
      window.ydProductSequence2Mobile = {
        phase1,
        phase3,
        planetScrub,
        timeController,
        phase1Loader,
        phase3Loader
      };

      // 后台加载帧（不阻塞 ScrollTrigger 创建）
      const phase1End = (config.totalFrames || 51) - 1;
      phase1Loader.preloadFrameRange(0, phase1End, config.priorityFrames || 30).then(() => {
        // 帧加载完成后，渲染当前帧
        if (phase1.currentFrame >= 0) {
          phase1.renderFrame(phase1.currentFrame, phase1.lastOpacity, phase1.lastScale);
        }
        phase1Loader.continuePreload();
      });

      const phase3TotalFrames = phase3Config.totalFrames || 351;
      const phase3Priority = phase3Config.priorityFrames || 50;
      phase3Loader.preloadFrameRange(0, phase3TotalFrames - 1, phase3Priority).then(() => {
        // 帧加载完成后，渲染当前帧
        if (phase3.currentFrame >= 0) {
          phase3.renderFrame(phase3.currentFrame, true);
        }
        phase3Loader.continuePreload();
      });

    } catch {
      // 初始化失败，静默处理
    }
  }

  // 等待 DOM 加载
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
